import { isNil } from 'lodash-es'

import { formatCurrency, useSession } from '@/shared/lib'
import { Separator, Skeleton, Description } from '@/shared/ui'

import { useContract } from './provider'
import { useContractEstimate } from '../../../api/get-contract-estimate'

type Props = {
  roomId: number
}

export const PriceInformation = ({ roomId }: Props) => {
  const { period, hasPeriodSelected } = useContract()
  const session = useSession()
  const isLoggedIn = !!session.data

  const { estimate, isLoading } = useContractEstimate({
    roomId,
    period: period,
    isExtend: false,
    enabled: hasPeriodSelected && isLoggedIn,
  })

  if (isLoading) {
    return <PriceInformationSkeleton />
  }

  if (isNil(estimate)) {
    return null
  }

  const showLongTermDiscount = estimate.longTermDiscount > 0
  const showEarlyDiscount = estimate.earlyDiscount > 0

  return (
    <div className="space-y-3 px-2.5">
      <Description.List>
        <Description.Item className="justify-between">
          <Description.ItemLabel normal>임대료</Description.ItemLabel>
          <Description.ItemValue>
            {formatCurrency(estimate.usingFee)}원
          </Description.ItemValue>
        </Description.Item>

        {showLongTermDiscount && (
          <Description.Item className="justify-between">
            <Description.ItemLabel normal highlight>
              장기계약 할인
            </Description.ItemLabel>
            <Description.ItemValue highlight>
              {formatCurrency(estimate.longTermDiscount, { negative: true })}원
            </Description.ItemValue>
          </Description.Item>
        )}
        {showEarlyDiscount && (
          <Description.Item className="justify-between">
            <Description.ItemLabel normal highlight>
              즉시입주 할인
            </Description.ItemLabel>
            <Description.ItemValue highlight>
              {formatCurrency(estimate.earlyDiscount, { negative: true })}원
            </Description.ItemValue>
          </Description.Item>
        )}

        <Description.Item className="justify-between">
          <Description.ItemLabel normal>관리비</Description.ItemLabel>
          <Description.ItemValue>
            {formatCurrency(estimate.mgmtFee)}원
          </Description.ItemValue>
        </Description.Item>

        <Description.Item className="items-start justify-between">
          <Description.ItemLabel normal>청소비</Description.ItemLabel>
          <Description.ItemValue>
            {formatCurrency(estimate.cleanFee)}원
          </Description.ItemValue>
        </Description.Item>

        <Description.Item className="justify-between">
          <Description.ItemLabel normal>계약 수수료</Description.ItemLabel>
          <Description.ItemValue>
            {formatCurrency(estimate.contractFee)}원
          </Description.ItemValue>
        </Description.Item>
      </Description.List>

      <Separator />

      <Description.List>
        <Description.Item className="justify-between">
          <Description.ItemLabel semiBold className="text-black">
            실 이용 금액
          </Description.ItemLabel>
          <Description.ItemValue bold highlight className="typo-body1">
            {formatCurrency(estimate.realUsingFee)}원
          </Description.ItemValue>
        </Description.Item>

        <Description.Item className="justify-between">
          <Description.ItemLabel normal>보증금</Description.ItemLabel>
          <Description.ItemValue>
            {formatCurrency(estimate.deposit)}원
          </Description.ItemValue>
        </Description.Item>

        <p className="typo-caption1 text-center font-normal text-black">
          * 보증금은 삼삼엠투에서 보관하며, 퇴실 후 반환됩니다.
        </p>
      </Description.List>

      <Separator color="black" />

      <Description.List>
        <Description.Item className="justify-between">
          <Description.ItemLabel semiBold className="text-black">
            최종 결제 금액
          </Description.ItemLabel>
          <Description.ItemValue bold className="typo-body1 text-black">
            {formatCurrency(estimate.finalPayFee)}원
          </Description.ItemValue>
        </Description.Item>
      </Description.List>
    </div>
  )
}

export const PriceInformationSkeleton = () => {
  return (
    <div className="space-y-3 px-2.5">
      <Description.List>
        {Array.from({ length: 6 }).map((_, index) => (
          <Description.Item key={index} className="justify-between">
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-5 w-16" />
          </Description.Item>
        ))}
      </Description.List>

      <Separator />

      <Description.List>
        {Array.from({ length: 2 }).map((_, index) => (
          <Description.Item key={index} className="justify-between">
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-5 w-16" />
          </Description.Item>
        ))}

        <Skeleton className="h-4 w-full" />
      </Description.List>

      <Separator color="black" />

      <Description.List>
        <Description.Item className="justify-between">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-18" />
        </Description.Item>
      </Description.List>
    </div>
  )
}
